/**
 * Per-project billing snapshot for the Billing & Payables hub.
 *
 * Groups receivables (client invoices / installments), payables (supplier
 * bills), and open vendor quotes so finance can see money in vs money out
 * per project in one place.
 */

import type {
  ChangeOrder,
  EnhancedDeal,
  Milestone,
  QBInvoice,
  RfqProject,
  RfqSubmission,
  SalesOrder,
  SupplierOrg,
  TimberProject,
} from '../types';
import type { PurchaseRequisition } from '../types/purchasing';
import type { SupplierBill } from './supplierBills';
import { isAchPurchasePayment, isCardPurchasePayment } from './purchasingPolicy';
import { isValidTimestamp } from './leadsHubUtils';
import {
  collapseDuplicateCncAwardBills,
  expandSupplierBillPayments,
  roundMoney,
  supplierBillPaidAmount,
  SUPPLIER_BILL_CATEGORY_LABELS,
  SUPPLIER_BILL_STATUS_LABELS,
} from './supplierBills';
import {
  compareReadyToBillBillingChronology,
  isReceivableSettled,
  type BillingReadyItem,
  type ReadyToBillRow,
} from './readyToBill';
import { getProjectDisplayName, parseLegacyTitle } from './projectName';
import { normalizeWorkType } from './vendorHelpers';
import { awardedQuotesMissingFromBills } from './awardedQuoteForecastGaps';

export type ProjectMoneyDirection = 'in' | 'out' | 'quote';

export type ProjectMoneyStatusKind =
  | 'paid'
  | 'invoiced'
  | 'billable'
  | 'upcoming'
  | 'ready'
  | 'pending'
  | 'quoted'
  | 'dismissed';

export interface ProjectMoneyLine {
  key: string;
  direction: ProjectMoneyDirection;
  label: string;
  detail: string;
  amount: number;
  statusLabel: string;
  statusKind: ProjectMoneyStatusKind;
  dueDate?: Date | null;
  estimated?: boolean;
  /** Present when this line maps to a Ready to Bill row (mark-ready actions). */
  receivableRow?: ReadyToBillRow;
  supplierBill?: SupplierBill;
  rfqSubmissionId?: string;
  poNumber?: string;
  purchaseRequisitionId?: string;
}

export interface ProjectBillingTotals {
  contractValue: number;
  receivableOutstanding: number;
  receivablePaidOrInvoiced: number;
  payableOpen: number;
  payablePaid: number;
  openQuotes: number;
}

export interface ProjectBillingSummary {
  dealId: string;
  dealLabel: string;
  projectNumber?: string;
  lines: ProjectMoneyLine[];
  totals: ProjectBillingTotals;
}

/** Synthetic deal id for OpEx / overhead supplier bills (no job dealId). */
export const OVERHEAD_BILLING_DEAL_ID = '__office_overhead__';
export const OVERHEAD_BILLING_DEAL_LABEL = 'Office & overhead';

export function isOverheadBillingDealId(dealId: string | null | undefined): boolean {
  return dealId === OVERHEAD_BILLING_DEAL_ID;
}

export interface BuildProjectBillingSummariesInputs {
  deals: EnhancedDeal[];
  timberProjects?: TimberProject[];
  salesOrders?: Array<SalesOrder & { id: string }>;
  coSalesOrders?: Array<SalesOrder & { id: string }>;
  qbInvoices?: Array<QBInvoice & { id: string }>;
  milestones?: Milestone[];
  changeOrders?: ChangeOrder[];
  trackedItems?: BillingReadyItem[];
  /** Full money-in rows (use buildReadyToBillQueue with forProjectSummary). */
  moneyInRows: ReadyToBillRow[];
  supplierBills: SupplierBill[];
  rfqSubmissions: RfqSubmission[];
  /** Issued job POs — used to promote linked Other Quotes to ready-to-pay. */
  purchaseRequisitions?: PurchaseRequisition[];
  /** Issued OpEx POs — Bill Payment rows with no job dealId land on the overhead card. */
  overheadPurchaseRequisitions?: PurchaseRequisition[];
  supplierOrgs: SupplierOrg[];
  /** CRM award state — flags quotes approved before the supplier-bill flow existed. */
  rfqProjects?: Array<
    Pick<
      RfqProject,
      | 'id'
      | 'approvedSubmissionIds'
      | 'approvedSubmissionIdsByRelease'
      | 'approvedSupplierOrgId'
      | 'approvedSupplierOrgIdByRelease'
      | 'approvedSupplierOrgIdsByRelease'
    >
  >;
  showDismissedReceivables: boolean;
  activeProjectsOnly?: boolean;
  /** Live task end dates — resolves task-anchored supplier payment terms. */
  taskEndDateById?: Map<string, Date>;
}

function resolveDealLabel(
  dealId: string,
  deal: EnhancedDeal | undefined,
  project: TimberProject | undefined,
  receivableRows: ReadyToBillRow[] | undefined,
  salesOrder: (SalesOrder & { id: string }) | undefined,
): string {
  if (deal) {
    const fromDeal = getProjectDisplayName(deal) || (deal.title || '').trim();
    if (fromDeal) return fromDeal;
  }

  const fromReceivable = receivableRows?.find(
    (r) => r.dealLabel && r.dealLabel !== dealId,
  )?.dealLabel;
  if (fromReceivable) return fromReceivable;

  if (project) {
    const num = (deal?.projectNumber || '').trim();
    const numberDisplay = num ? (num.startsWith('#') ? num : `#${num}`) : '';
    const descriptor = (
      (deal?.descriptor || '').trim()
      || parseLegacyTitle(deal?.title || '').descriptor
      || (project.name || '').trim()
    ).trim();
    const clientLabel = (
      (deal?.clientLabel || '').trim()
      || (project.clientName || '').trim()
      || (deal?.contactName || '').trim()
    ).trim();
    if (numberDisplay && (clientLabel || descriptor)) {
      return [numberDisplay, clientLabel, descriptor].filter(Boolean).join(' ').trim();
    }
    const combined = [clientLabel, descriptor].filter(Boolean).join(' ').trim();
    if (combined) return combined;
    const projectName = (project.name || project.clientName || '').trim();
    if (projectName) return projectName;
  }

  const clientName = (salesOrder?.clientName || '').trim();
  if (clientName) return clientName;

  return dealId;
}

/** Fully settled invoice or supplier bill. Open quotes and unpaid balances stay in the working list. */
export function isBillingHistoryLine(line: ProjectMoneyLine): boolean {
  if (line.direction === 'quote') return false;
  if (line.supplierBill?.status === 'void') return false;
  return line.statusKind === 'paid' || line.supplierBill?.status === 'paid';
}

/** True when this project card still has unpaid AP staff can mark paid. */
function summaryHasOpenPayable(summary: ProjectBillingSummary): boolean {
  return summary.lines.some(
    (line) => line.direction === 'out' && line.statusKind !== 'paid',
  );
}

/**
 * Default Billing & Payables scope — open Under Contract timber projects only
 * (won deal + non-completed project where phase is under contract).
 * Estimating, preconstruction, and completed jobs are hidden unless they
 * still have open supplier bills. Designer invoices stay in the Billings Hub
 * and are not listed again as project payables.
 */
export function isDealInDefaultBillingProjectScope(
  dealId: string,
  timberProjects: TimberProject[],
  deals: EnhancedDeal[],
): boolean {
  const deal = deals.find((d) => d.id === dealId);
  // Lost deals stay out of Billing & Payables. Won is the common case; also
  // accept active / legacy "open" when the Hub project is already under contract
  // (CRM #57 / #59 — numbered jobs whose outcome never flipped to won).
  const outcome = String(deal?.outcome || '').toLowerCase();
  if (!deal || outcome === 'lost') return false;

  return timberProjects.some(
    (p) =>
      p.dealId === dealId
      && p.status !== 'completed'
      && (p.phase === 'contracted' || p.phase === undefined),
  );
}

function orgName(orgs: SupplierOrg[], orgId: string): string {
  return orgs.find((o) => o.id === orgId)?.name?.trim() || orgId;
}

function receivableStatus(row: ReadyToBillRow): { label: string; kind: ProjectMoneyStatusKind } {
  const tracked = row.tracked?.status;
  if (tracked === 'dismissed') return { label: 'Dismissed', kind: 'dismissed' };
  // CRM-marked paid (or a synced QB paid flag) wins over an open invoice
  // number — otherwise the pill stays "Invoiced #…" after finance marks paid.
  if (tracked === 'paid' || row.qbPaid) return { label: 'Paid', kind: 'paid' };
  if (row.qbInvoiced) {
    const num = row.quickbooksInvoiceNum || row.tracked?.quickbooksInvoiceNum;
    return { label: num ? `Invoiced #${num}` : 'Invoiced in QB', kind: 'invoiced' };
  }
  if (tracked === 'sent') return { label: 'Sent to client', kind: 'invoiced' };
  if (tracked === 'invoiced') {
    const num = row.quickbooksInvoiceNum || row.tracked?.quickbooksInvoiceNum;
    return { label: num ? `Invoiced #${num}` : 'Invoiced in QB', kind: 'invoiced' };
  }
  if (tracked === 'ready') return { label: 'Ready to bill', kind: 'ready' };
  if (row.billableNow) return { label: 'Billable now', kind: 'billable' };
  return { label: 'Upcoming', kind: 'upcoming' };
}

function quoteLabel(sub: RfqSubmission, orgs: SupplierOrg[]): string {
  const work = normalizeWorkType(sub.workType);
  const workLabel =
    work === 'cnc_quote' ? 'CNC' : work === 'other_quote' ? 'Other' : 'Timber';
  const vendor = orgName(orgs, sub.supplierOrgId);
  const scope = sub.quoteLabel?.trim() || sub.quotedSpecies?.trim();
  return scope ? `${workLabel} — ${vendor} (${scope})` : `${workLabel} — ${vendor}`;
}

function quoteDetail(sub: RfqSubmission): string {
  const parts: string[] = [];
  if (sub.staffManualUpload) parts.push('Staff-uploaded quote');
  else if (sub.legacyImport) parts.push('Legacy quote');
  else parts.push('Supplier portal quote');
  if (sub.quoteValidUntil instanceof Date && isValidTimestamp(sub.quoteValidUntil)) {
    parts.push(`Valid until ${sub.quoteValidUntil.toLocaleDateString()}`);
  }
  return parts.join(' · ');
}

function isIssuedJobPurchase(row: Pick<PurchaseRequisition, 'status' | 'poNumber'>): boolean {
  return !!row.poNumber || row.status === 'approved' || row.status === 'purchased';
}

/** Designer-invoice POs are Billings Hub bills, not per-project payables. */
export function supplierBillIsDesignerInvoice(
  bill: Pick<SupplierBill, 'purchaseRequisitionId' | 'poNumber'>,
  poById: ReadonlyMap<string, Pick<PurchaseRequisition, 'designerInvoiceId'>>,
  poByNumber: ReadonlyMap<string, Pick<PurchaseRequisition, 'designerInvoiceId'>>,
): boolean {
  const prId = bill.purchaseRequisitionId?.trim();
  if (prId && poById.get(prId)?.designerInvoiceId?.trim()) return true;
  const number = bill.poNumber?.trim();
  return !!(number && poByNumber.get(number)?.designerInvoiceId?.trim());
}

function issuedPurchaseForQuote(
  sub: RfqSubmission,
  byId: Map<string, PurchaseRequisition>,
  bySubmissionId: Map<string, PurchaseRequisition>,
): PurchaseRequisition | undefined {
  const linked = sub.purchaseRequisitionId ? byId.get(sub.purchaseRequisitionId) : undefined;
  // Designer-invoice POs are lines on the approved invoice bill, not an award.
  if (linked && !linked.designerInvoiceId?.trim() && isIssuedJobPurchase(linked)) return linked;
  if (sub.id) {
    const bySub = bySubmissionId.get(sub.id);
    if (bySub && !bySub.designerInvoiceId?.trim() && isIssuedJobPurchase(bySub)) return bySub;
  }
  return undefined;
}

function isCardIssuedPurchase(
  row: Pick<PurchaseRequisition, 'paymentMethod' | 'paymentMethodId'>,
): boolean {
  return isCardPurchasePayment({ id: row.paymentMethodId, label: row.paymentMethod });
}

export function payableStatusForBill(bill: SupplierBill): { label: string; kind: ProjectMoneyStatusKind } {
  if (bill.status === 'paid') return { label: SUPPLIER_BILL_STATUS_LABELS.paid, kind: 'paid' };
  if (bill.status === 'pending_review') {
    return { label: SUPPLIER_BILL_STATUS_LABELS.pending_review, kind: 'pending' };
  }
  return {
    label: SUPPLIER_BILL_STATUS_LABELS[bill.status] || bill.status,
    kind: 'ready',
  };
}

export { collapseDuplicateCncAwardBills } from './supplierBills';

function moneyLinesForSupplierBills(args: {
  bills: SupplierBill[];
  poByNumber: Map<string, PurchaseRequisition>;
  taskEndDateById?: Map<string, Date>;
}): ProjectMoneyLine[] {
  const lines: ProjectMoneyLine[] = [];
  for (const bill of args.bills) {
    if (!bill.id) continue;
    const isPaid = bill.status === 'paid';
    const explicitDue =
      bill.dueDate instanceof Date ? bill.dueDate : bill.dueDate ? new Date(bill.dueDate) : null;
    let dueDate: Date | null = explicitDue;
    let estimated = false;
    if (!dueDate && bill.paymentTerms?.installments?.length) {
      const dated = expandSupplierBillPayments(bill, {
        taskEndDateById: args.taskEndDateById,
      })
        .filter(
          (s) =>
            s.dueDate &&
            !(s.installmentIndex !== undefined && bill.installmentPayments?.[s.installmentIndex]),
        )
        .sort((a, b) => a.dueDate!.getTime() - b.dueDate!.getTime());
      if (dated[0]) {
        dueDate = dated[0].dueDate;
        estimated = !isPaid;
      }
    }
    const payable = payableStatusForBill(bill);
    lines.push({
      key: `out:${bill.id}`,
      direction: 'out',
      label: SUPPLIER_BILL_CATEGORY_LABELS[bill.category] || bill.category,
      detail: `${bill.vendorName}${bill.description ? ` — ${bill.description}` : ''}`,
      amount: roundMoney(Number(bill.amount) || 0),
      statusLabel: payable.label,
      statusKind: payable.kind,
      dueDate,
      estimated,
      supplierBill: bill,
      poNumber: bill.poNumber?.trim() || undefined,
      purchaseRequisitionId:
        bill.purchaseRequisitionId?.trim()
        || (bill.poNumber ? args.poByNumber.get(bill.poNumber.trim())?.id : undefined),
    });
  }
  return lines;
}

function payableTotalsForBills(bills: SupplierBill[]): Pick<
  ProjectBillingTotals,
  'payableOpen' | 'payablePaid'
> {
  const payableOpen = bills
    .filter((b) => b.status !== 'paid' && b.status !== 'void')
    .reduce((s, b) => s + Math.max(0, (Number(b.amount) || 0) - supplierBillPaidAmount(b)), 0);
  const payablePaid = bills
    .filter((b) => b.status !== 'void')
    .reduce((s, b) => s + supplierBillPaidAmount(b), 0);
  return { payableOpen, payablePaid };
}

export function buildProjectBillingSummaries(
  inputs: BuildProjectBillingSummariesInputs,
): ProjectBillingSummary[] {
  const dealById = new Map(
    inputs.deals.filter((d) => d.id).map((d) => [d.id!, d] as const),
  );
  const projectByDealId = new Map(
    (inputs.timberProjects ?? [])
      .filter((p) => p.dealId)
      .map((p) => [p.dealId!, p] as const),
  );
  const salesOrderByDealId = new Map(
    (inputs.salesOrders ?? [])
      .filter((so) => !so.isChangeOrder && so.dealId)
      .map((so) => [so.dealId!, so] as const),
  );

  const receivableByDeal = new Map<string, ReadyToBillRow[]>();
  for (const row of inputs.moneyInRows) {
    if (row.tracked?.status === 'dismissed' && !inputs.showDismissedReceivables) continue;
    const list = receivableByDeal.get(row.dealId) ?? [];
    list.push(row);
    receivableByDeal.set(row.dealId, list);
  }

  const awardedQuoteGaps = awardedQuotesMissingFromBills({
    rfqProjects: inputs.rfqProjects ?? [],
    rfqSubmissions: inputs.rfqSubmissions,
    supplierBills: inputs.supplierBills,
  });
  const missingAwardSubmissionIds = new Set(
    awardedQuoteGaps.slices.map((slice) => slice.submissionId),
  );
  const omitAwardBillIds = new Set(awardedQuoteGaps.omitBillIds);

  const jobPos = inputs.purchaseRequisitions ?? [];
  const poById = new Map(jobPos.map((row) => [row.id, row]));
  const poByNumber = new Map<string, PurchaseRequisition>();
  const poBySubmissionId = new Map<string, PurchaseRequisition>();
  for (const row of jobPos) {
    const submissionId = row.rfqSubmissionId?.trim();
    if (submissionId) poBySubmissionId.set(submissionId, row);
    const number = row.poNumber?.trim();
    if (number) poByNumber.set(number, row);
  }

  const billsByDeal = new Map<string, SupplierBill[]>();
  for (const bill of collapseDuplicateCncAwardBills(inputs.supplierBills)) {
    if (bill.id && omitAwardBillIds.has(bill.id)) continue;
    if (!bill.dealId || bill.status === 'void') continue;
    const list = billsByDeal.get(bill.dealId) ?? [];
    list.push(bill);
    billsByDeal.set(bill.dealId, list);
  }

  const billedSubmissionIds = new Set(
    inputs.supplierBills
      .map((b) => b.rfqSubmissionId)
      .filter(Boolean) as string[],
  );
  const billedRequisitionIds = new Set(
    inputs.supplierBills
      .map((b) => b.purchaseRequisitionId)
      .filter(Boolean) as string[],
  );
  const billedCncOrgKeys = new Set(
    inputs.supplierBills
      .filter((b) => b.status !== 'void' && b.category === 'cnc' && b.rfqProjectId && b.supplierOrgId)
      .map((b) => `${b.rfqProjectId}:${b.supplierOrgId}`),
  );

  // Award state lives on the rfqProject docs: per-submission ids for timber,
  // per-org ids for CNC. Quotes approved before the supplier-bill flow existed
  // have no bill doc, so this is the only signal that they were awarded.
  const approvedSubmissionIds = new Set<string>();
  const approvedOrgIdsByRfqProject = new Map<string, Set<string>>();
  for (const rp of inputs.rfqProjects ?? []) {
    for (const id of rp.approvedSubmissionIds ?? []) approvedSubmissionIds.add(id);
    for (const ids of Object.values(rp.approvedSubmissionIdsByRelease ?? {})) {
      for (const id of ids) approvedSubmissionIds.add(id);
    }
    const orgIds = new Set<string>();
    if (rp.approvedSupplierOrgId) orgIds.add(rp.approvedSupplierOrgId);
    for (const id of Object.values(rp.approvedSupplierOrgIdByRelease ?? {})) {
      if (id) orgIds.add(id);
    }
    for (const ids of Object.values(rp.approvedSupplierOrgIdsByRelease ?? {})) {
      for (const id of ids) orgIds.add(id);
    }
    if (rp.id && orgIds.size) approvedOrgIdsByRfqProject.set(rp.id, orgIds);
  }
  const isQuoteApproved = (sub: RfqSubmission): boolean => {
    if (sub.id && approvedSubmissionIds.has(sub.id)) return true;
    if (!sub.rfqProjectId || !sub.supplierOrgId) return false;
    return approvedOrgIdsByRfqProject.get(sub.rfqProjectId)?.has(sub.supplierOrgId) ?? false;
  };

  const quotesByDeal = new Map<string, RfqSubmission[]>();
  for (const sub of inputs.rfqSubmissions) {
    if (!sub.dealId || sub.notBidding || sub.rejectedAt) continue;
    const amount = roundMoney(Number(sub.quoteTotal) || 0);
    if (amount <= 0) continue;
    if (sub.id && billedSubmissionIds.has(sub.id) && !missingAwardSubmissionIds.has(sub.id)) continue;
    if (sub.purchaseRequisitionId && billedRequisitionIds.has(sub.purchaseRequisitionId)) continue;
    const issued = issuedPurchaseForQuote(sub, poById, poBySubmissionId);
    if (issued && billedRequisitionIds.has(issued.id)) continue;
    if (issued && isCardIssuedPurchase(issued)) continue;
    const missingOwnBill = !!sub.id && missingAwardSubmissionIds.has(sub.id);
    if (!missingOwnBill && sub.supplierOrgId && billedSubmissionIds.has(`org:${sub.supplierOrgId}`)) continue;
    if (
      !missingOwnBill
      && normalizeWorkType(sub.workType) === 'cnc_quote'
      && sub.rfqProjectId
      && sub.supplierOrgId
      && billedCncOrgKeys.has(`${sub.rfqProjectId}:${sub.supplierOrgId}`)
    ) {
      continue;
    }
    const list = quotesByDeal.get(sub.dealId) ?? [];
    list.push(sub);
    quotesByDeal.set(sub.dealId, list);
  }

  const dealIds = new Set<string>([
    ...Array.from(receivableByDeal.keys()),
    ...Array.from(billsByDeal.keys()),
    ...Array.from(quotesByDeal.keys()),
  ]);

  const summaries: ProjectBillingSummary[] = [];

  Array.from(dealIds).forEach((dealId) => {
    const deal = dealById.get(dealId);
    const receivables = receivableByDeal.get(dealId) ?? [];
    const dealLabel = resolveDealLabel(
      dealId,
      deal,
      projectByDealId.get(dealId),
      receivables,
      salesOrderByDealId.get(dealId),
    );
    const lines: ProjectMoneyLine[] = [];
    receivables.sort(compareReadyToBillBillingChronology);

    for (const row of receivables) {
      const status = receivableStatus(row);
      lines.push({
        key: `in:${row.key}`,
        direction: 'in',
        label: row.label,
        detail: row.detail,
        amount: row.amount,
        statusLabel: status.label,
        statusKind: status.kind,
        dueDate: row.dueDate,
        // Forecast dates stay "estimated" until finance commits the row by
        // marking it ready for billing (or QB shows it invoiced/paid).
        estimated: !row.tracked && !row.qbInvoiced && !row.qbPaid,
        receivableRow: row,
      });
    }

    const bills = (billsByDeal.get(dealId) ?? []).slice().sort((a, b) => {
      const at = a.dueDate instanceof Date ? a.dueDate.getTime() : Number.MAX_SAFE_INTEGER;
      const bt = b.dueDate instanceof Date ? b.dueDate.getTime() : Number.MAX_SAFE_INTEGER;
      return at - bt;
    });

    lines.push(
      ...moneyLinesForSupplierBills({
        bills,
        poByNumber,
        taskEndDateById: inputs.taskEndDateById,
      }),
    );

    const quotes = quotesByDeal.get(dealId) ?? [];
    quotes.sort((a, b) => (b.createdAt?.getTime?.() ?? 0) - (a.createdAt?.getTime?.() ?? 0));

    const quoteIsIssuedReady = (sub: RfqSubmission): boolean => {
      if (sub.poNumber?.trim()) return true;
      return !!issuedPurchaseForQuote(sub, poById, poBySubmissionId);
    };
    const quoteCountsAsPayable = (sub: RfqSubmission): boolean =>
      quoteIsIssuedReady(sub) || isQuoteApproved(sub);

    for (const sub of quotes) {
      if (!sub.id) continue;
      const issued = issuedPurchaseForQuote(sub, poById, poBySubmissionId);
      const poNumber = issued?.poNumber?.trim() || sub.poNumber?.trim() || undefined;
      const issuedReady = quoteIsIssuedReady(sub);
      const approved = isQuoteApproved(sub);
      lines.push({
        key: `quote:${sub.id}`,
        direction: 'quote',
        label: quoteLabel(sub, inputs.supplierOrgs),
        detail: quoteDetail(sub),
        amount: roundMoney(Number(sub.quoteTotal) || 0),
        statusLabel: issuedReady
          ? SUPPLIER_BILL_STATUS_LABELS.ready_to_pay
          : approved
            ? 'Approved — no bill yet'
            : 'Quoted — not awarded',
        statusKind: issuedReady || approved ? 'ready' : 'quoted',
        rfqSubmissionId: sub.id,
        poNumber,
        purchaseRequisitionId:
          issued?.id
          || sub.purchaseRequisitionId?.trim()
          || (poNumber ? poByNumber.get(poNumber)?.id : undefined),
      });
    }

    if (lines.length === 0) return;

    const receivableOutstanding = receivables
      .filter((r) => !isReceivableSettled(r))
      .reduce((s, r) => s + r.amount, 0);
    const receivablePaidOrInvoiced = receivables
      .filter((r) => r.qbPaid || r.qbInvoiced || r.tracked?.status === 'paid' || r.tracked?.status === 'invoiced')
      .reduce((s, r) => s + r.amount, 0);
    // Approved quotes are committed spend even without a bill doc yet — count
    // them with payables, not with quotes still awaiting a decision.
    const billPayables = payableTotalsForBills(bills);
    const payableOpen =
      billPayables.payableOpen
      + quotes
        .filter((q) => quoteCountsAsPayable(q))
        .reduce((s, q) => s + (Number(q.quoteTotal) || 0), 0);
    const payablePaid = billPayables.payablePaid;
    const openQuotes = quotes
      .filter((q) => !quoteCountsAsPayable(q))
      .reduce((s, q) => s + (Number(q.quoteTotal) || 0), 0);

    summaries.push({
      dealId,
      dealLabel,
      projectNumber: deal?.projectNumber,
      lines,
      totals: {
        contractValue: receivables.reduce((s, r) => s + r.amount, 0),
        receivableOutstanding,
        receivablePaidOrInvoiced,
        payableOpen,
        payablePaid,
        openQuotes,
      },
    });
  });

  const overheadPrIds = new Set(
    (inputs.overheadPurchaseRequisitions ?? []).map((row) => row.id).filter(Boolean),
  );
  const overheadBills = collapseDuplicateCncAwardBills(inputs.supplierBills)
    .filter((bill) => {
      if (bill.status === 'void') return false;
      if (!bill.dealId?.trim()) return true;
      const prId = bill.purchaseRequisitionId?.trim();
      return !!prId && overheadPrIds.has(prId);
    })
    .slice()
    .sort((a, b) => {
      const at = a.dueDate instanceof Date ? a.dueDate.getTime() : Number.MAX_SAFE_INTEGER;
      const bt = b.dueDate instanceof Date ? b.dueDate.getTime() : Number.MAX_SAFE_INTEGER;
      return at - bt;
    });
  const overheadBillLines = moneyLinesForSupplierBills({
    bills: overheadBills,
    poByNumber,
    taskEndDateById: inputs.taskEndDateById,
  });
  const overheadBilledPrIds = new Set(
    overheadBills.map((bill) => bill.purchaseRequisitionId?.trim()).filter(Boolean) as string[],
  );
  for (const pr of inputs.overheadPurchaseRequisitions ?? []) {
    if (pr.status !== 'approved') continue;
    if (!pr.poNumber?.trim()) continue;
    if (overheadBilledPrIds.has(pr.id)) continue;
    if (
      !isAchPurchasePayment({ id: pr.paymentMethodId, label: pr.paymentMethod })
    ) {
      continue;
    }
    overheadBillLines.push({
      key: `opex-po:${pr.id}`,
      direction: 'out',
      label: SUPPLIER_BILL_CATEGORY_LABELS.other,
      detail: `${pr.vendorName?.trim() || 'Vendor'}${pr.title ? ` — ${pr.title}` : ''}`,
      amount: roundMoney(Number(pr.amount) || 0),
      statusLabel: 'Needs payable record',
      statusKind: 'pending',
      poNumber: pr.poNumber.trim(),
      purchaseRequisitionId: pr.id,
    });
  }
  if (overheadBillLines.length > 0) {
    const { payableOpen, payablePaid } = payableTotalsForBills(overheadBills);
    const missingPayableOpen = (inputs.overheadPurchaseRequisitions ?? [])
      .filter(
        (pr) =>
          pr.status === 'approved'
          && pr.poNumber?.trim()
          && !overheadBilledPrIds.has(pr.id)
          && isAchPurchasePayment({ id: pr.paymentMethodId, label: pr.paymentMethod }),
      )
      .reduce((sum, pr) => sum + roundMoney(Number(pr.amount) || 0), 0);
    summaries.push({
      dealId: OVERHEAD_BILLING_DEAL_ID,
      dealLabel: OVERHEAD_BILLING_DEAL_LABEL,
      lines: overheadBillLines,
      totals: {
        contractValue: 0,
        receivableOutstanding: 0,
        receivablePaidOrInvoiced: 0,
        payableOpen: payableOpen + missingPayableOpen,
        payablePaid,
        openQuotes: 0,
      },
    });
  }

  summaries.sort((a, b) => {
    if (a.dealId === OVERHEAD_BILLING_DEAL_ID && b.dealId !== OVERHEAD_BILLING_DEAL_ID) return -1;
    if (b.dealId === OVERHEAD_BILLING_DEAL_ID && a.dealId !== OVERHEAD_BILLING_DEAL_ID) return 1;
    const aBillable = a.lines.some((l) => l.receivableRow?.billableNow);
    const bBillable = b.lines.some((l) => l.receivableRow?.billableNow);
    if (aBillable !== bBillable) return aBillable ? -1 : 1;
    return a.dealLabel.localeCompare(b.dealLabel);
  });

  if (inputs.activeProjectsOnly !== false) {
    return summaries.filter((s) => {
      if (isOverheadBillingDealId(s.dealId)) return summaryHasOpenPayable(s);
      return (
        isDealInDefaultBillingProjectScope(
          s.dealId,
          inputs.timberProjects ?? [],
          inputs.deals,
        ) || summaryHasOpenPayable(s)
      );
    });
  }

  return summaries;
}
